import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { UnitSearchResult } from 'api';
import { getUnitTitle } from 'api/responses/models/Unit/UnitStructure/Unit';
import {
	HighlightText,
	alphaNumericLowerCaseNormalizer,
	getIdentifiersRegexpSearch,
} from 'library/components/controls/display/HighlightText';
import { textOverflowSx } from 'library/utils/themes/PCTheme';
import { isUnitIndustrial } from 'modules/Shared/State/unit';

export type UnitTitleProps = Omit<
	UnitSearchResult,
	| 'city'
	| 'country'
	| 'ownerCompanyName'
	| 'ownerId'
	| 'ownerName'
	| 'ownerSurname'
	| 'ownerEmail'
	| 'isOwnerAnonymous'
	| 'installations'
	| 'serviceAgreementEndDate'
> & {
	highlightedText?: string | null;
};

/**
 * Component that renders unit title and highlighted identifiers.
 *
 * @param props - The unit search result including name, brand/model, and optional highlightedText.
 * @returns JSX.Element representing the unit title.
 */
export const UnitTitle = (props: UnitTitleProps): React.JSX.Element => {
	const { name, brandOrType, model, highlightedText = '' } = props;
	const unitTitle = getUnitTitle(name, brandOrType, model) || '\xa0';

	return (
		<Box sx={{ pt: 0.5 }}>
			<Typography title={unitTitle} variant="body2" sx={textOverflowSx}>
				{/* Highlight only the "name" part */}
				<HighlightText text={name} phrase={highlightedText} />
				{unitTitle.slice(name?.length || 0)}
			</Typography>
			<Typography title="identifiers" variant="body2" sx={textOverflowSx}>
				{getHighlightedIdentifiers(props, highlightedText || '')}
			</Typography>
		</Box>
	);
};

/**
 * Extracts and highlights identifier parts from unit properties.
 *
 * @param props - The unit properties containing identification numbers.
 * @param phrase - The phrase to highlight within the identifiers.
 * @returns React.ReactNode displaying the highlighted identifier(s).
 */
const getHighlightedIdentifiers = (
	props: UnitTitleProps,
	phrase: string
): React.ReactNode => {
	if (isUnitIndustrial(props.mainSegment)) {
		return (
			<HighlightText
				text={props.hullMachineId}
				phrase={phrase}
				normalizer={alphaNumericLowerCaseNormalizer}
				regexpSearch={getIdentifiersRegexpSearch(phrase)}
			/>
		);
	} else {
		const { hullId, imoNumber, mmsiNumber, callSign } = props;
		const values = [hullId, imoNumber, mmsiNumber, callSign].filter(
			(val): val is string => !!val
		);
		return values.map((val, idx) => (
			<React.Fragment key={val}>
				<HighlightText
					text={val}
					phrase={phrase}
					normalizer={alphaNumericLowerCaseNormalizer}
					regexpSearch={getIdentifiersRegexpSearch(phrase)}
				/>
				{idx < values.length - 1 ? ', ' : ''}
			</React.Fragment>
		));
	}
};
